
import { useState, useEffect } from 'react';
import { useSupabaseAuth } from '@/hooks/auth';
import { supabase } from '@/integrations/supabase/client';

interface RealProgressData {
  moduleProgress: number;
  courseProgress: number;
  timeSpent: number;
  estimatedTime: number;
  completedModules: number;
  totalModules: number;
  streak: number;
  skillPoints: number;
  isLoading: boolean;
}

export const useRealProgressData = (moduleId: string, courseId: string): RealProgressData => {
  const { user, profile } = useSupabaseAuth();
  const [progressData, setProgressData] = useState<RealProgressData>({
    moduleProgress: 0,
    courseProgress: 0,
    timeSpent: 0,
    estimatedTime: 60,
    completedModules: 0,
    totalModules: 0,
    streak: 0,
    skillPoints: 0,
    isLoading: true
  });

  useEffect(() => {
    if (!user || !moduleId || !courseId) {
      setProgressData(prev => ({ ...prev, isLoading: false }));
      return;
    }

    const fetchRealProgressData = async () => {
      try {
        // Get module progress
        const moduleProgress = profile?.progress?.[moduleId] || 0;

        // Calculate course progress from all modules in this course
        const courseModules = Object.keys(profile?.progress || {}).filter(id => 
          id.startsWith(courseId.split('-')[0])
        );
        const completedModules = courseModules.filter(id => 
          profile?.progress?.[id] === 100
        ).length;
        const courseProgress = courseModules.length > 0 
          ? Math.round((completedModules / courseModules.length) * 100) 
          : 0;

        // Get time spent from activity logs
        const { data: activityData } = await supabase
          .from('module_activity_logs')
          .select('time_spent')
          .eq('user_id', user.id)
          .eq('module_id', moduleId);

        const totalTimeSpent = activityData?.reduce((sum, log) => sum + (log.time_spent || 0), 0) || 0;

        // Get learning streak from activity logs
        const { data: recentActivity } = await supabase
          .from('module_activity_logs')
          .select('created_at')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(30);

        // Calculate streak (simplified - consecutive days with activity)
        let streak = 0;
        if (recentActivity && recentActivity.length > 0) {
          const today = new Date();
          let currentDate = new Date(today);
          
          for (let i = 0; i < 30; i++) {
            const dayActivity = recentActivity.find(activity => {
              const activityDate = new Date(activity.created_at);
              return activityDate.toDateString() === currentDate.toDateString();
            });
            
            if (dayActivity) {
              streak++;
              currentDate.setDate(currentDate.getDate() - 1);
            } else {
              break;
            }
          }
        }

        // Calculate skill points based on completed modules and quiz scores
        const { data: attemptData } = await supabase
          .from('module_attempts')
          .select('score, max_score, passed')
          .eq('user_id', user.id);

        const skillPoints = attemptData?.reduce((points, attempt) => {
          if (attempt.passed) {
            const percentage = (attempt.score / attempt.max_score) * 100;
            return points + Math.round(percentage / 10); // 10 points per 10%
          }
          return points;
        }, 0) || 0;

        setProgressData({
          moduleProgress,
          courseProgress,
          timeSpent: Math.round(totalTimeSpent / 60), // Convert to minutes
          estimatedTime: 60, // This could be fetched from module metadata
          completedModules,
          totalModules: courseModules.length,
          streak,
          skillPoints,
          isLoading: false
        });

      } catch (error) {
        console.error('Error fetching progress data:', error);
        setProgressData(prev => ({ ...prev, isLoading: false }));
      }
    };

    fetchRealProgressData();
  }, [user, profile, moduleId, courseId]);

  return progressData;
};
